import {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useTheme, styled} from '@mui/material/styles'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import {useGetAssignableSubtasks} from './hooks/useGetAssignableSubtasks'
import {FormikErrors, FormikValues} from 'formik'
import {Task} from '../core/_models'
import {useMutation, useQueryClient} from 'react-query'
import {deliveryTask, playTimeTracking, stopTimeTracking} from '../core/_requests'
import {Toast} from '../../../../../../_metronic/partials/widgets'
import {QUERIES} from '../../../../../../_metronic/helpers'
import {useSearchParams} from 'react-router-dom'
import {useDeleteSubtask} from './hooks/useDeleteSubtask'
import {useCreateSubtask} from './hooks/useCreateSubtask'
import {useDeleteTask} from './hooks/useDeleteTask'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import Swal from 'sweetalert2'
import {useCanDeleteTask} from '../../../../config/hooks/useCanDeleteTask'
import {SubtaskUsersSymbolGroupColumn} from './components/SubtaskUsersSymbolGroupColumn'
import {UsersSymbolGroup} from '../../projects-list/UsersSymbolGroup'
import {useAuth} from '../../../../auth'

type SubtasksProps = {
  tasks?: Task[]
  formikSetFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<FormikErrors<FormikValues>> | Promise<void>
  usersAll: any[]
  tabId?: string
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const {disablePortal, anchorEl, open, ...other} = props
  return <StyledAutocompletePopper {...other} />
}
//@ts-ignore
const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: `0px 0px 0px 3px ${
        theme.palette.mode === 'light' ? 'rgba(3, 102, 214, 0.3)' : 'rgb(12, 45, 107)'
      }`,
      borderColor: theme.palette.mode === 'light' ? '#0366d6' : '#388bfd',
    },
  },
}))

export function Subtasks({formikSetFieldValue, tasks, usersAll, tabId}: SubtasksProps) {
  const intl = useIntl()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [subtasks, setSubtasks] = useState(tasks ?? ([] as Task[]))
  const {data: assignableSubtasks} = useGetAssignableSubtasks({search})
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [subtaskName, setSubtaskName] = useState('')
  const {mutateAsync: deleteSubtaskFn} = useDeleteSubtask()
  const {mutateAsync: deleteTaskFn} = useDeleteTask()
  const {mutateAsync: createSubtaskFn} = useCreateSubtask()
  const {canDelete: canDeleteGlobal, config} = useCanDeleteTask()
  const {currentUser} = useAuth()

  // Função helper para verificar se pode deletar uma tarefa específica
  const canDeleteTask = (task: Task) => {
    if (!config) return true // Se não há config, permite por padrão

    const onlyManagerAdminDeleteTasks = config.only_manager_admin_delete_tasks || false
    if (!onlyManagerAdminDeleteTasks) return true

    const isManager = Boolean(currentUser?.manager)
    const isAdmin = Boolean(currentUser?.administrator)
    if (isManager || isAdmin) return true

    const allowCreatorDeleteOwnTasks = config.allow_creator_delete_own_tasks || false
    if (allowCreatorDeleteOwnTasks && task.creator_id) {
      const currentUserId = currentUser?.id
      const taskCreatorId = typeof task.creator_id === 'string' 
        ? parseInt(task.creator_id) 
        : task.creator_id
      return currentUserId === taskCreatorId
    }

    return false
  }
  const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
  const colorTheme = CurrentTheme === 'dark' ? '#353b48' : '#fff'

  useEffect(() => {
    if (tasks) setSubtasks(tasks)
  }, [tasks])

  useEffect(() => {
    if (!assignableSubtasks || assignableSubtasks.length === 0) return

    setSubtasks((prevSubtasks) =>
      prevSubtasks.map((subtask) => {
        const updated = assignableSubtasks.find(
          //@ts-ignore
          (a) => a.id === subtask.id || a.id === subtask.user_id
        )
        return updated ? {...subtask, users: updated.users} : subtask
      })
    )
  }, [assignableSubtasks])

  const playItem = useMutation((taskId: number) => playTimeTracking(taskId), {
    onSuccess: (response, taskId) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_TIMETRACKING_START'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TIMETRACKING'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      queryClient.setQueryData(
        [`${QUERIES.TASKS_LIST}-task-${searchParams.get('taskId')}`],
        (task: any) => {
          const updatedSubtasks = task.tasks.subtasks.map((subtask: any) => ({
            ...subtask,
            has_active_play: subtask.id === taskId ? 1 : 0,
          }))

          return {
            ...task,
            tasks: {
              ...task.tasks,
              subtasks: updatedSubtasks,
            },
          }
        }
      )

      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(QUERIES.TASKS_LIST),
      })
    },
    onError: (error: any) => {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Ocorreu um erro inesperado',
        customClass: {
          popup: 'bg-light-danger',
          title: theme.palette.mode === 'dark' ? 'text-dark' : 'text-danger',
        },
      })
    },
  })

  const stopItem = useMutation((taskId: number) => stopTimeTracking(taskId), {
    onSuccess: (response, taskId) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_TIMETRACKING_STOP'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TIMETRACKING'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      queryClient.setQueryData(
        [`${QUERIES.TASKS_LIST}-task-${searchParams.get('taskId')}`],
        (task: any) => {
          const updatedSubtasks = task.tasks.subtasks.map((subtask: any) =>
            subtask.id === taskId ? {...subtask, has_active_play: 0} : subtask
          )

          return {
            ...task,
            tasks: {
              ...task.tasks,
              subtasks: updatedSubtasks,
            },
          }
        }
      )

      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(QUERIES.TASKS_LIST),
      })
    },
  })

  useEffect(() => {
    if (tasks) setSubtasks(tasks)
  }, [tasks])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
  }

  function handleUnlink(id?: number, index?: number) {
    Swal.fire({
      title: intl.formatMessage({id: 'ALERT.LABEL.ARE_YOU_SURE'}),
      html: 'Você deseja disvincular esta subtarefa da tarefa principal?',
      icon: 'warning',
      background: colorTheme,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Sim, pode desvincular',
      cancelButtonText: 'Não, cancele!',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        if (id) {
          deleteSubtaskFn(id)
        }

        if (index !== undefined) {
          setSubtasks((prev) => prev.filter((subtask, indexSubtask) => indexSubtask != index))
        }
      }
    })
  }

  function handleDelete(id?: number, index?: number) {
    // Verifica se pode deletar a tarefa específica
    const task = id ? subtasks.find(t => t.id === id) : (index !== undefined ? subtasks[index] : null)
    const canDeleteThisTask = task ? canDeleteTask(task) : canDeleteGlobal

    if (!canDeleteThisTask) {
      Swal.fire({
        title: 'Acesso negado',
        html: 'Você não tem permissão para excluir esta tarefa.',
        icon: 'error',
        background: colorTheme,
        showCloseButton: true,
        confirmButtonText: 'Entendi',
      })
      return
    }

    Swal.fire({
      title: 'Você tem certeza?',
      html: 'Você tem certeza que deseja remover esta subtarefa? Essa ação irá excluí-la permanentemente e todos os dados serão perdidos.',
      icon: 'warning',
      background: colorTheme,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Sim, pode excluir',
      cancelButtonText: 'Não, cancele!',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        if (id) {
          deleteTaskFn(id)
        }

        if (index !== undefined) {
          setSubtasks((prev) => prev.filter((subtask, indexSubtask) => indexSubtask != index))
        }
      }
    })
  }

  function handlePlay(id: number) {
    playItem.mutateAsync(id)
  }

  function handlePause(id: number) {
    stopItem.mutateAsync(id)
  }

  function handleOpenTaskModal(id: number) {
    const params = new URLSearchParams(searchParams)
    params.set('taskId', String(id))
    setSearchParams(params)
  }

  const delivery = useMutation((taskId: number) => deliveryTask(taskId), {
    onSuccess: (response, taskId) => {
      Toast.fire({
        icon: response.status === 'success' ? 'success' : 'error',
        title: response.message,
        customClass: {
          popup: response.status === 'success' ? 'bg-light-success' : 'bg-light-error',
          title: theme.palette.mode === 'dark' ? 'text-dark' : 'text-success',
        },
      })

      queryClient.setQueryData(
        [`${QUERIES.TASKS_LIST}-task-${searchParams.get('taskId')}`],
        (task: any) => {
          const updatedSubtasks = task.tasks.subtasks.map((subtask: any) =>
            subtask.id === taskId
              ? {...subtask, end_date: new Date().toLocaleDateString('pt-BR')}
              : subtask
          )

          return {
            ...task,
            tasks: {
              ...task.tasks,
              subtasks: updatedSubtasks,
            },
          }
        }
      )
    },
    onError: (error: any) => {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Ocorreu um erro inesperado',
        customClass: {
          popup: 'bg-light-danger',
          title: theme.palette.mode === 'dark' ? 'text-dark' : 'text-danger',
        },
      })
    },
  })

  function handleDelivery(id: number) {
    delivery.mutateAsync(id)
  }

  async function handleCreateSubtask() {
    const taskId = searchParams.get('taskId')

    let newValue = taskId
      ? // @ts-ignore
        ({
          name: subtaskName,
          parent_id: taskId,
        } as Task)
      : ({
          name: subtaskName,
        } as Task)

    if (taskId) {
      createSubtaskFn(newValue)
    } else {
      setSubtasks([...subtasks, newValue])
      formikSetFieldValue('subtasks', [...subtasks, newValue])
    }

    setSubtaskName('')
  }

  const content = (
    <div className='pt-6 subtasks-container' id='subtasks'>
      <div className='d-flex align-items-center justify-content-center me-3 gap-3'>
        <input
          type='text'
          className='flex-grow-1 form-control form-control-sm border-0 rounded bg-gray-100 bg-focus-gray-100 ms-1'
          placeholder='Crie uma subtarefa e aperte enter para adicionar'
          value={subtaskName}
          onChange={(e) => setSubtaskName(e.target.value)}
          onKeyDown={(e) => {
            if (subtaskName == '') return
            if (e.key === 'Enter') {
              handleCreateSubtask()
            }
          }}
        />
        <button
          className='btn btn-primary btn-sm'
          onClick={handleCreateSubtask}
          disabled={!subtaskName.trim()}
          style={{whiteSpace: 'nowrap'}}
        >
          <i className='bi bi-plus-lg me-1'></i>
          Adicionar
        </button>
        <button
          className='btn btn-secondary btn-sm'
          onClick={handleClick}
          style={{whiteSpace: 'nowrap'}}
        >
          <i className='bi bi-list-ul me-1'></i>
          Buscar
        </button>
      </div>

      <StyledPopper
        id={assignableSubtasks?.toString()}
        open={open}
        anchorEl={anchorEl}
        placement='bottom-end'
        disablePortal={true}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={{
                borderBottom: `1px solid ${
                  theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
                }`,
                padding: '8px 10px',
                fontWeight: 600,
              }}
            >
              Incluir subtarefas
            </Box>
            <Autocomplete
              inputValue={search}
              open
              multiple
              onClose={(event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleClose()
                }
              }}
              onInputChange={(event, value) => {
                setSearch(value)
              }}
              onChange={(event, newValue, reason, details) => {
                if (
                  event.type === 'keydown' &&
                  (event as React.KeyboardEvent).key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return
                }

                const taskId = searchParams.get('taskId')
                if (taskId) {
                  // @ts-ignore
                  createSubtaskFn({id: details?.option?.id, parent_id: taskId})
                } else {
                  setSubtasks([...subtasks, details?.option] as Task[])
                  formikSetFieldValue('subtasks', [...subtasks, details?.option])
                }
              }}
              disableCloseOnSelect
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText='Nenhuma tarefa encontrada.'
              renderOption={(props, option, {selected}) => (
                <li {...props} key={option.id}>
                  <Box
                    component={DoneIcon}
                    sx={{width: 17, height: 17, mr: '15px', ml: '-2px'}}
                    style={{
                      visibility: subtasks?.some((subtask) => subtask.id === option.id)
                        ? 'visible'
                        : 'hidden',
                    }}
                  />

                  <Box
                    sx={{
                      flexGrow: 1,
                      '& span': {
                        color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                      },
                    }}
                  >
                    {option.name}
                    <br />
                  </Box>

                  <Box
                    component={CloseIcon}
                    sx={{opacity: 0.6, width: 18, height: 18}}
                    style={{
                      visibility: subtasks?.some((subtask) => subtask.id === option.id)
                        ? 'visible'
                        : 'hidden',
                    }}
                  />
                </li>
              )}
              options={
                assignableSubtasks?.sort((a, b) => {
                  let ai = assignableSubtasks.indexOf(a)
                  ai = ai === -1 ? assignableSubtasks.length + assignableSubtasks.indexOf(a) : ai
                  let bi = assignableSubtasks.indexOf(b)
                  bi = bi === -1 ? assignableSubtasks.length + assignableSubtasks.indexOf(b) : bi
                  return ai - bi
                }) || []
              }
              getOptionLabel={(option) => option.name!}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={'Selecione uma subtarefa'}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>

      <div className='checklist pt-6'>
        <div className='checklist-items'>
          {subtasks.map((task, index) => (
            <div key={task.id ?? index} className='check d-flex align-items-center' style={{paddingLeft: '40px'}}>
              <div
                className='flex-grow-1 d-flex align-items-center'
                onClick={() => {
                  return task.id ? handleOpenTaskModal(Number(task.id)) : null
                }}
                role='button'
              >
                <span className='me-2 text-muted' style={{fontSize: '0.875rem'}}>
                  <i className='bi bi-arrow-return-right'></i>
                </span>
                <span className='form-control border-0 bg-transparent p-0' style={{flex: 1}}>
                  {task.name}
                </span>
              </div>

              {!searchParams.get('taskId') && (
                <>
                  {!!task.id ? (
                    <SubtaskUsersSymbolGroupColumn
                      usersAll={usersAll}
                      classes='justify-content-end'
                      users={task.users as []}
                      placement={'bottom-end'}
                      taskId={task.id}
                    />
                  ) : (
                    <UsersSymbolGroup
                      users={usersAll}
                      classes='justify-content-end'
                      onChange={(users) => {
                        const updatedSubtasks = subtasks.map((t, tIndex) =>
                          index === tIndex ? {...t, users} : t
                        )
                        formikSetFieldValue('subtasks', updatedSubtasks)
                        setSubtasks(updatedSubtasks)
                      }}
                      responsibles={task.users}
                      placement={'bottom-end'}
                      hasDefaultValue={false}
                    />
                  )}
                </>
              )}

              {searchParams.get('taskId') && (
                <>
                  <SubtaskUsersSymbolGroupColumn
                    usersAll={usersAll}
                    classes='justify-content-end'
                    users={task.users as []}
                    placement={'bottom-end'}
                    taskId={task.id}
                  />

                  {!task.end_date && (
                    <>
                      {task.has_active_play ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip id={`tooltip-pause-${task.id}`}>Parar</Tooltip>}
                        >
                          <button
                            onClick={() => handlePause(Number(task.id))}
                            className='btn p-0 ms-3'
                            style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
                          >
                            <i className='bi bi-pause-circle text-success fs-4'></i>
                          </button>
                        </OverlayTrigger>
                      ) : (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip id={`tooltip-play-${task.id}`}>Iniciar</Tooltip>}
                        >
                          <button
                            onClick={() => handlePlay(Number(task.id))}
                            className='btn p-0 ms-3'
                            style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
                          >
                            <i className='bi bi-play-circle text-primary fs-4'></i>
                          </button>
                        </OverlayTrigger>
                      )}
                    </>
                  )}

                  {!!task.end_date ? (
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip id={`tooltip-entregue-${task.id}`}>Tarefa entregue</Tooltip>
                      }
                    >
                      <button
                        className='btn p-0 ms-3'
                        style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
                      >
                        <i className='bi bi-check-lg fs-4 text-primary'></i>
                      </button>
                    </OverlayTrigger>
                  ) : (
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip id={`tooltip-entregar-${task.id}`}>Entregar tarefa</Tooltip>
                      }
                    >
                      <button
                        onClick={() => handleDelivery(Number(task.id))}
                        className='btn p-0 ms-3'
                        style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
                      >
                        <i className='bi bi-check-lg fs-4'></i>
                      </button>
                    </OverlayTrigger>
                  )}
                </>
              )}

              {!!searchParams.get('taskId') && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id={`tooltip-unlink-${task.id}`}>Desvincular da tarefa pai</Tooltip>
                  }
                >
                  <button
                    onClick={() =>
                      task.id ? handleUnlink(Number(task.id)) : handleUnlink(undefined, index)
                    }
                    className='btn p-0 ms-3'
                    style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
                  >
                    <i className='bi bi-unlock-fill fs-4'></i>
                  </button>
                </OverlayTrigger>
              )}

              {canDeleteTask(task) && (
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip id={`tooltip-delete-${task.id}`}>Excluir tarefa</Tooltip>}
                >
                  <button
                    onClick={() =>
                      task.id ? handleDelete(Number(task.id)) : handleDelete(undefined, index)
                    }
                    className='btn p-0 ms-3'
                    style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
                  >
                    <i className='bi bi-trash3-fill text-danger fs-4'></i>
                  </button>
                </OverlayTrigger>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  return tabId ? (
    <div id={tabId} className='tab-pane fade'>
      {content}
    </div>
  ) : (
    content
  )
}
